<template>
	<div>
		<stepper
			v-bind:steps="['Account', 'Cause', 'Preferences', 'Funding']"
			:currentStep="2"
			v-if="onboarding"
		/>
		<screen
			class="institutions-view"
			type="blue"
			:style="{
				overflowY: filterOpen ? 'hidden' : 'scroll',
				maxHeight: filterOpen ? '100vh' : 'none',
				minHeight: '100vh'
			}"
		>
			<template slot="header">
				<ui-header type="light" @click="onClose" action="close">
					<h2 slot="title">Select Organization</h2>
				</ui-header>
			</template>
			<div class="institutions-search">
				<div class="roundup-row">
					<div style="flex: 4; position: relative">
						<ui-input
							type="blue"
							v-model="query"
							placeholder="Search"
						/>
						<div v-if="listLoading" class="loading" />
					</div>
					<div
						class="filterIcon"
						style="flex: 1"
						@click="toggleFilter"
					>
						<img
							src="@/assets/filter_icon.png"
							height="20"
							width="17.1"
						/>
						<p>Filters</p>
					</div>
				</div>
			</div>
			<div
				class="roundup-row"
				style="
					justify-content: flex-start;
					width: 100%;
					flex-wrap: wrap;
					margin-top: 5px;
				"
			>
				<filter-tag
					v-for="loc in filterLocs"
					:key="loc"
					@click="updateAndSaveChanges(loc, 'location')"
					>{{ loc }}</filter-tag
				>
				<filter-tag
					v-for="cat in filterCats"
					:key="cat"
					@click="updateAndSaveChanges(cat, 'category')"
					>{{ cat }}</filter-tag
				>
			</div>
			<div class="institutions-list" style="overflow-y: scroll">
				<div
					v-for="item in institutions"
					:key="item.id"
					@click="openInstitution(item)"
					class="item"
				>
					<img :src="item.logo_url" />
					<div class="item-text">
						<span class="item-name">{{ item.name }}</span>
						<span class="item-subtitle">{{
							item.impact_statement
						}}</span>
					</div>
				</div>
			</div>
			<institution-view v-model="institution" />
			<div v-bind:class="{ handleOpened: filterOpen }">
				<transition name="slide">
					<div
						class="slidein"
						v-if="filterOpen"
						:style="{
							marginBottom: '0px',
							maxHeight: maxFilterHeight
						}"
					>
						<ui-header
							type="light"
							v-on:click="toggleFilter"
							action="close-dark"
						>
							<h3>Filters</h3>
						</ui-header>
						<div class="roundup-row filterTabs">
							<div
								style="margin-right: 10px; cursor: pointer"
								@click="toggleFilterTabs"
							>
								<div
									:class="['locationActive', filterActive]"
								/>
								<p
									:style="{
										color:
											filterActive === 'location'
												? '#2553CD'
												: '#71788C'
									}"
								>
									Location
								</p>
							</div>
							<div
								style="margin-left: 10px; cursor: pointer"
								@click="toggleFilterTabs"
							>
								<div
									:class="'categoryActive ' + filterActive"
								/>
								<p
									:style="{
										color:
											filterActive === 'category'
												? '#2553CD'
												: '#71788C'
									}"
								>
									Category
								</p>
							</div>
						</div>
						<div
							v-if="filterActive === 'location'"
							class="filterList"
							:style="{
								paddingBottom: '250px'
							}"
						>
							<div
								v-for="state in states"
								:key="state.code"
								@click="updateFilter(state.code, 'location')"
								:class="[
									'item',
									shouldBeActive(state.code, 'location') &&
										'active'
								]"
							>
								<p>{{ state.name }}</p>
							</div>
						</div>
						<div
							v-else
							class="filterList"
							:style="{
								paddingBottom: hasUnsavedChanges && '250px'
							}"
						>
							<div
								v-for="(cat, idx) in categories"
								:key="idx"
								@click="updateFilter(cat.title, 'category')"
								:class="[
									'item',
									shouldBeActive(cat.title, 'category') &&
										'active'
								]"
							>
								<p>{{ cat.title }}</p>
							</div>
						</div>
						<div class="roundup-row bottom-bar">
							<ui-button
								class="white border"
								style="
									height: 40px;
									margin-right: 10px;
									font-size: 18px;
									padding: revert;
								"
								@click="resetChanges"
								>CLEAR ALL</ui-button
							>
							<ui-button
								style="
									height: 40px;
									margin-left: 10px;
									font-size: 18px;
									padding: revert;
								"
								@click="saveChanges"
								>APPLY</ui-button
							>
						</div>
					</div>
				</transition>
			</div>
		</screen>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import routable from '../../mixins/routable'
import UiInput from '../../ui/Input'
import UiHeader from '../../ui/Header'
import UiButton from '../../ui/Button'
import FilterTag from '../../ui/FilterTag'
import Screen from '../../ui/Screen'
import InstitutionView from './InstitutionView'
import Stepper from '../../ui/Stepper'
import { states } from '../../utilities/enums'

export default {
	name: 'institutions-view',
	components: {
		Screen,
		UiInput,
		UiHeader,
		InstitutionView,
		Stepper,
		UiButton,
		FilterTag
	},
	mixins: [routable],
	data() {
		return {
			query: '',
			filtered: [],
			institution: null,
			onboarding:
				this.$route.params.onboarding || this.$route.meta.onboarding,
			filterOpen: false,
			filterActive: 'location',
			filterTags: [],
			states: states,
			filterLocs: [],
			filterCats: [],
			unsavedChanges: {
				locs: false,
				cats: false,
				filterLocs: [],
				filterCats: []
			}
		}
	},
	computed: {
		...mapGetters(['institutions', 'listLoading', 'categories']),
		hasUnsavedChanges() {
			return (
				this.unsavedChanges.filterLocs.length +
					this.unsavedChanges.filterCats.length >
					0 ||
				this.unsavedChanges.locs ||
				this.unsavedChanges.cats
			)
		},
		allLocs() {
			return this.unsavedChanges.filterLocs.concat(this.filterLocs)
		},
		allCats() {
			return this.unsavedChanges.filterCats.concat(this.filterCats)
		},
		filterString() {
			let ans = ''
			if (this.filterLocs.length) {
				ans +=
					'location:' +
					JSON.stringify(this.filterLocs).replace(/"/g, '').trim()
			}
			if (this.filterCats.length) {
				if (ans.length) {
					ans += '%26%26'
				}
				ans +=
					'category:' +
					JSON.stringify(this.filterCats).replace(/"/g, '').trim()
			}
			return ans
		},
		maxFilterHeight() {
			if (window.innerWidth > 450) {
				return '100%'
			}
			if (this.onboarding) {
				return 'calc(100vh - 65px)'
			}
			return '100vh'
		}
	},
	methods: {
		...mapActions([
			'getInstitutions',
			'searchInstitutions',
			'attachInstitution',
			'getCategories',
			'logout'
		]),
		openInstitution(item) {
			this.institution = item
		},
		selectedInstitution(item) {
			this.attachInstitution(item)
				.then(() => {
					this.next()
				})
				.catch((error) => {
					this.$swal(error.message)
				})
		},
		search({ query, filters }) {
			if (query.length <= 0 && this.filterString.length === 0) {
				return
			}
			this.searchInstitutions({
				query,
				filters
			})
		},
		onClose() {
			if (this.$route.name == 'settings.institution') {
				this.$router.push({ name: 'settings' }, 'back')
			} else {
				if (confirm('Would you like to log out?')) {
					this.logout().then(() => {
						this.$router.push('logout')
					})
				}
			}
		},
		toggleFilter() {
			this.filterOpen = !this.filterOpen
		},
		toggleFilterTabs() {
			this.filterActive =
				this.filterActive === 'location' ? 'category' : 'location'
		},
		updateFilter(code, type) {
			let dataArr = 'filterLocs'
			if (type !== 'location') {
				dataArr = 'filterCats'
			}
			let currList
			let codeIdx
			if (this.hasUnsavedChanges) {
				currList = [...this.unsavedChanges[dataArr]]
			} else {
				if (type === 'location') {
					currList = [...this.allLocs]
				} else {
					currList = [...this.allCats]
				}
			}
			codeIdx = currList.indexOf(code)

			if (codeIdx > -1) {
				currList.splice(codeIdx, 1)
			} else {
				currList.push(code)
			}
			this.unsavedChanges[dataArr] = currList
			if (type === 'location') {
				this.unsavedChanges.locs = true
			} else {
				this.unsavedChanges.cats = true
			}
			if (
				JSON.stringify(this.unsavedChanges[dataArr].sort()) ===
				JSON.stringify(this[dataArr].sort())
			) {
				this.unsavedChanges.locs = false
				this.unsavedChanges.cats = false
			}
		},
		resetChanges() {
			this.unsavedChanges = {
				locs: false,
				cats: false,
				filterLocs: [],
				filterCats: []
			}
			this.filterCats = []
			this.filterLocs = []
			this.filterOpen = false
		},
		saveChanges() {
			if (this.unsavedChanges.locs) {
				this.filterLocs = this.unsavedChanges.filterLocs
			}
			if (this.unsavedChanges.cats) {
				this.filterCats = this.unsavedChanges.filterCats
			}
			this.search({
				query: this.query,
				filters: this.filterString
			})
			this.filterOpen = false
		},
		updateAndSaveChanges(code, type) {
			this.updateFilter(code, type)
			this.saveChanges()
		},
		shouldBeActive(code, type) {
			let dataArr = 'filterLocs'
			let typeChanges = 'locs'
			if (type !== 'location') {
				dataArr = 'filterCats'
				typeChanges = 'cats'
			}

			if (this.hasUnsavedChanges && this.unsavedChanges[typeChanges]) {
				return this.unsavedChanges[dataArr].includes(code)
			}
			return this[dataArr].includes(code)
		}
	},
	mounted() {
		this.getInstitutions().then(() => {
			this.filtered = this.institutions
		})
		this.getCategories()
		this.bottomOffset = this.onboarding ? '65px' : '0px'
	},
	watch: {
		query: function (val) {
			this.search({
				query: val,
				filters: this.filterString
			})
		}
	}
}
</script>

<style lang="scss">
.institutions-view {
	height: 80vh;
	overflow-y: scroll;
	.institutions-list {
		margin-top: 10px;
		.item {
			border-bottom: 1px solid rgba(255, 255, 255, 0.2);
			padding: 15px 0;
			display: flex;
			text-align: left;
			list-style: none;
			cursor: pointer;
			img {
				width: 40px;
				height: 40px;
				border-radius: 8px;
			}
			.item-text {
				padding-left: 15px;
				span {
					display: block;
					color: $white;
					&.item-subtitle {
						opacity: 0.5;
						font-size: 13px;
					}
				}
			}
		}
		@media screen and (max-width: 480px) {
			height: inherit;
		}
	}
	.institutions-search {
		.loading {
			padding-right: 55px;
			&:before {
				content: '';
				position: absolute;
				display: block;
				width: 0px;
				height: 0px;
				border-radius: 50%;
				right: 15px;
				top: 25px;
				border: 2px solid rgba(255, 255, 255, 0.8);
				border-right: 2px solid $navy;
				animation: rotate360 0.5s infinite linear,
					exist 0.1s forwards ease;
			}
		}
	}
	.filterIcon {
		display: block;
		min-width: 25px;
		justify-content: center;
		align-items: center;
		&:hover {
			cursor: pointer;
		}
		p {
			font-size: 10px;
			color: #fff;
			margin-top: 0px;
			margin-bottom: 0px;
		}
	}
	.slidein {
		max-width: 600px;
		padding: 0em;
		padding-bottom: 2em;
		position: absolute;
		z-index: 100;
		bottom: 0;
		right: 0;
		left: 0;
		background: #fff;
		height: 100%;
		box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.5);
		transition: all 0.5s ease-in-out;
		h3 {
			margin-top: 2em;
			font-family: Work Sans, sans-serif;
			font-size: 24px;
			text-align: center;
			color: $dark-grey;
		}
		.filterTabs {
			box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.1);
			padding-bottom: 20px;
			p {
				margin-top: 5px;
				margin-bottom: 5px;
				font-weight: 600;
				color: $dark-grey;
			}
			.locationActive {
				height: 100px;
				width: 150px;
				background: url('../../assets/location_filter_default.png')
					no-repeat;
				background-size: contain;
				&.location {
					background: url('../../assets/location_filter_active.png')
						no-repeat;
					background-size: contain;
				}
			}
			.categoryActive {
				height: 100px;
				width: 150px;
				background: url('../../assets/category_filter_default.png')
					no-repeat;
				background-size: contain;
				&.category {
					background: url('../../assets/category_filter_active.png')
						no-repeat;
					background-size: contain;
				}
			}
		}
		.filterList {
			padding: 1em 2em;
			overflow-y: scroll;
			max-height: 90%;
			margin-bottom: 150px;
			.item {
				margin-top: 10px;
				margin-bottom: 10px;
				padding-left: 25px;
				border: 1px solid #dcdee3;
				border-radius: 5px;
				height: 60px;
				font-size: 16px;
				p {
					margin: 0px;
					line-height: 60px;
				}
				&.active {
					border: 1px solid $navy;
					font-weight: 600;
					color: $navy;
					background: #ebf1ff;
				}
			}
		}
		.bottom-bar {
			position: absolute;
			z-index: 101;
			bottom: 0;
			height: 77px;
			width: 100%;
			box-shadow: 0px -2px 3px rgba(0, 0, 0, 0.1);
			background: #fff;
			padding: 20px 22px;
		}
	}
	@media screen and (max-width: 480px) {
		top: 0;
	}

	.slide-enter,
	.slide-leave-active {
		bottom: -100%;
	}

	@keyframes rotate360 {
		100% {
			transform: rotate(360deg);
		}
	}

	@keyframes exist {
		100% {
			width: 12px;
			height: 12px;
			margin: -8px 5px 0 0;
		}
	}
	.institutions-actions {
		padding: 20px;
	}
}
</style>
